import {CopyOutlined, ExperimentOutlined, EyeOutlined, FileAddOutlined, FunctionOutlined, LoadingOutlined, MessageFilled, MessageOutlined} from "@ant-design/icons";
import {Alert, Breadcrumb, Button, Card, Collapse, Descriptions, Form, InputNumber, message, Modal, Spin, Table, Tag, Tooltip} from "antd";
import {useForm} from "antd/es/form/Form";
import React, {useContext, useEffect, useReducer, useRef, useState} from "react";
import {Link, useParams} from "react-router-dom";
import {AiExtractorServiceContext, AppContextContext, AttributeDefinitionServiceContext, VulSourceItemServiceContext} from "../../Contexts";
import VulSourceItem, {AiExtractionResult} from "../../domain/VulSourceItem";
import {formatBytesBinary, formatDateTime, syntaxHighlight, utf8ByteLength} from "../../utils/FormatUtils";
import {normalizeNonEmptyString} from "../../utils/StringUtils";
import CreateTicketModal from "../ticket/CreateTicketModal";
import ItemVote from "./ItemVote";
import * as styles from "./VulSourceItemDetail.module.css";
import * as globalStyles from "../App.module.css";
import {DocumentTitle} from "../DocumentTitle";
import AttributeDefinition from "../../domain/AttributeDefinition";
import QueryOptions from "../../sal-ui/QueryOptions";
import VulSourceItemDescription from "./VulSourceItemDescription";
import Column from "antd/es/table/Column";
import VulSourceItemChangeLog from "../../domain/VulSourceItemChangeLog";
import {VulSourceItemChangeType} from "../../domain/VulSourceItemChangeType";
import {VulSourceItemChangeKind} from "../../domain/VulSourceItemChangeKind";
import SealScore from "./SealScore";
import SourceTag from "./SourceTag";
import Ticket from "../../domain/Ticket";
import {Permission} from "../../domain/auth/Permission";
import AiExtractor from "../../domain/AiExtractor";
import AiExtractorSelectModal from "./AiExtractorSelectModal";
import AiExtractionResultsSection from "./AiExtractionResultsSection";
import {AiExtractDryRunResponse, AiProviderCommunicationLoggingMode, ItemUpdateProfileChangeLogMatch, PromptPreviewEstimate, PromptPreviewMessage} from "../../service/VulSourceItemService";
import PromptPreviewModal from "./PromptPreviewDrawer";
import AiAttributeEntriesBlock from "./AiAttributeEntriesBlock";
import JsonDifferenceModal from "../common/JsonDifferenceModal";
import {Feature} from "../../domain/Feature";
import {ResourceOwnerType} from "../../domain/ResourceOwnerType";
import AiProviderCommunicationLogModal from "../common/AiProviderCommunicationLogModal";

const LOCAL_STORAGE_KEY_RELATED_ITEMS_MAX_DEPTH = "Seal.VulSourceItemDetail.RelatedItemsMaxDepth";

const PREVIEW_HIGHLIGHT_DURATION_MS = 2000;

type DryRunPreviewAssignment = AiExtractionResult & {
    previewId: string;
    dryRunId?: string;
};

function VulSourceItemDetail() {
    const storedMaxDepth = localStorage.getItem(LOCAL_STORAGE_KEY_RELATED_ITEMS_MAX_DEPTH);

    const defaultRelatedItemsMaxDepth = (storedMaxDepth != null) ? parseInt(storedMaxDepth) : 1;

    const appContext = useContext(AppContextContext);
    const vulSourceItemService = useContext(VulSourceItemServiceContext);
    const attributeDefinitionService = useContext(AttributeDefinitionServiceContext);
    const aiExtractorService = useContext(AiExtractorServiceContext);
    const {vulSourceItemId}: any = useParams();
    const currentVulSourceItemIdRef = useRef<string | undefined>(undefined);
    const [vulSourceItem, setVulSourceItem] = useState<VulSourceItem>();
    const [jsonDataRaw, setJsonDataRaw] = useState<string | undefined>();
    const [jsonData, setJsonData] = useState<any>();
    const [jsonDataLoading, setJsonDataLoading] = useState(false);
    const [editForm] = useForm();
    const [extractResult, setExtractResult] = useState<any>();
    const [evaluateResults, setEvaluateResults] = useState<any>();
    const [attributeDefinitions, setAttributeDefinitions] = useState<AttributeDefinition[]>();
    const [createTicketFormVisible, setCreateTicketFormVisible] = useState<boolean>(false);
    const [relatedItemsMaxDepth, setRelatedItemsMaxDepth] = useState(defaultRelatedItemsMaxDepth);
    const [maxRelatedSealScore, setMaxRelatedSealScore] = useState<number>();
    const [relatedItems, setRelatedItems] = useState<VulSourceItem[]>();
    const [relatedTickets, setRelatedTickets] = useState<Ticket[]>();
    const [relatedItemsInProgress, setRelatedItemsInProgress] = useState(false);
    const [aiExtractPreviewAssignments, setAiExtractPreviewAssignments] = useState<DryRunPreviewAssignment[]>();
    const [aiExtractors, setAiExtractors] = useState<AiExtractor[]>([]);
    const [selectedAiExtractorId, setSelectedAiExtractorId] = useState<string | undefined>();
    const [aiExtractorSelectOpen, setAiExtractorSelectOpen] = useState(false);
    const [highlightedPreviewId, setHighlightedPreviewId] = useState<string | undefined>();
    const [globalMessagesVisible, setGlobalMessagesVisible] = useState(false);
    const [promptPreviewSelectOpen, setPromptPreviewSelectOpen] = useState(false);
    const [promptPreviewOpen, setPromptPreviewOpen] = useState(false);
    const [promptPreviewMessages, setPromptPreviewMessages] = useState<PromptPreviewMessage[] | undefined>();
    const [promptPreviewEstimate, setPromptPreviewEstimate] = useState<PromptPreviewEstimate | undefined>();
    const [promptPreviewLoading, setPromptPreviewLoading] = useState(false);
    const [promptPreviewAiExtractorName, setPromptPreviewAiExtractorName] = useState<string | undefined>();
    const [aiProviderCommunicationLogRunId, setAiProviderCommunicationLogRunId] = useState<string | undefined>();
    const [itemUpdateProfileMatchesOpen, setItemUpdateProfileMatchesOpen] = useState(false);
    const [itemUpdateProfileMatchesLoading, setItemUpdateProfileMatchesLoading] = useState(false);
    const [itemUpdateProfileMatches, setItemUpdateProfileMatches] = useState<ItemUpdateProfileChangeLogMatch[]>([]);
    const [itemUpdateProfileMatchesChangeLog, setItemUpdateProfileMatchesChangeLog] = useState<VulSourceItemChangeLog | undefined>();
    const [changeLogDataModalOpen, setChangeLogDataModalOpen] = useState(false);
    const [changeLogDataModalChangeLog, setChangeLogDataModalChangeLog] = useState<VulSourceItemChangeLog | undefined>();
    const [changeLogDifferenceModalOpen, setChangeLogDifferenceModalOpen] = useState(false);
    const [changeLogDifferenceModalChangeLog, setChangeLogDifferenceModalChangeLog] = useState<VulSourceItemChangeLog | undefined>();
    const [changeLogDifferenceOlderChangeLog, setChangeLogDifferenceOlderChangeLog] = useState<VulSourceItemChangeLog | undefined>();
    const previewHighlightTimeoutRef = useRef<number | undefined>(undefined);
    const lastScrolledPreviewIdRef = useRef<string | undefined>(undefined);

    const [, forceUpdate] = useReducer(x => x + 1, 0);

    const canManageOrgAiExtractors = appContext.user.hasFeature(Feature.AI) && (appContext.user.isSystemAdmin() || appContext.user.hasPermission(Permission.AI_EXTRACTOR__MANAGE_ORG));

    const runnableAiExtractors = canManageOrgAiExtractors
        ? aiExtractors.filter(aiExtractor => appContext.user.isSystemAdmin()
                                             || vulSourceItem?.source?.owner !== ResourceOwnerType.SYSTEM
                                             || aiExtractor.owner !== ResourceOwnerType.SYSTEM)
        : [];

    currentVulSourceItemIdRef.current = vulSourceItemId;

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        attributeDefinitionService.getList(QueryOptions.newUnlimitedOrderedInstance("name")).then(value => setAttributeDefinitions(value.data));

        reload();
        reloadRelatedItems();

        window.scrollTo({top: 0, left: 0, behavior: "smooth"})
    }, [vulSourceItemId])

    useEffect(() => {
        return () => {
            clearPreviewHighlightTimeout();
        };
    }, []);

    return (
        <DocumentTitle title={vulSourceItem?.id}>
            <>
                <Breadcrumb className={globalStyles["common__breadcrumb"]}
                            items={[
                                {title: appContext.config?.appName},
                                {title: <Link to={"/vul-source-items"}>Vulnerability items</Link>},
                                {title: vulSourceItem?.id}
                            ]}
                />

                <Spin spinning={!vulSourceItem} indicator={<LoadingOutlined style={{fontSize: 24}} spin={true}/>}>
                    <h1>{vulSourceItem?.attributes.title || vulSourceItem?.attributes?.description?.substring(0, 160) || vulSourceItem?.id}</h1>

                    <div className={globalStyles["common__top-button-bar"]}>
                        <div className={`${styles.vote}`} style={{background: "transparent"}}>
                            {vulSourceItem && <ItemVote onVote={updateItem} item={vulSourceItem}/>}
                        </div>

                        {appContext.user.isSystemAdmin() && <>
                            <Button icon={<ExperimentOutlined/>} onClick={() => onExtractDryConfirm(vulSourceItem)}>Extract (dry)</Button>

                            <Button icon={<FunctionOutlined/>} onClick={() => onEvaluateDryConfirm(vulSourceItem)}>Evaluate (dry)</Button>
                        </>}

                        {canManageOrgAiExtractors && (
                            <Button icon={<FunctionOutlined/>} onClick={() => onAiExtractDryConfirm(vulSourceItem)}>AI extract (dry)</Button>
                        )}

                        {appContext.user.isSystemAdmin() && (
                            <Button icon={<EyeOutlined/>} onClick={() => onPromptPreviewConfirm(vulSourceItem)}>Prompt preview</Button>
                        )}

                        {appContext.user.hasPermission(Permission.TICKET__MANAGE) &&
                            <Button icon={<FileAddOutlined/>}
                                    title={"Create ticket"}
                                    onClick={() => {
                                        setCreateTicketFormVisible(true);
                                        setVulSourceItem(vulSourceItem);
                                    }}>Create ticket
                            </Button>
                        }
                    </div>

                    {relatedTickets?.map(ticket => <Alert key={ticket.id} className={styles['ticket-alert']} type={"info"}
                                                          title={<>Related ticket: <Link to={`/tickets/${ticket.id}`}>{ticket.title}</Link></>}/>)}
                    <Descriptions column={1} bordered={true} size="small" className={styles.descriptions}>
                        <Descriptions.Item label={"Description"}>{renderDescriptionSection()}</Descriptions.Item>

                        <Descriptions.Item label={"Timestamp (SEAL)"}>
                            <Descriptions column={1} size={"small"} className={styles['nested-descriptions']}>
                                <Descriptions.Item label={"Created"}>{formatDateTime(vulSourceItem?.created)}</Descriptions.Item>
                                <Descriptions.Item label={"Data modification"}>{formatDateTime(vulSourceItem?.lastModified)}</Descriptions.Item>
                                <Descriptions.Item label={"Attribute modification"}>{formatDateTime(vulSourceItem?.lastModifiedAttributes)}</Descriptions.Item>
                            </Descriptions>
                        </Descriptions.Item>
                        <Descriptions.Item label={"Timestamp (source)"}>
                            <Descriptions column={1} size={"small"} className={styles['nested-descriptions']}>
                                <Descriptions.Item label={"Published"}>{formatDateTime(vulSourceItem?.sourcePublished)}</Descriptions.Item>
                                <Descriptions.Item label={"Last update"}>{formatDateTime(vulSourceItem?.sourceLastUpdate)}</Descriptions.Item>
                            </Descriptions>
                        </Descriptions.Item>

                        <Descriptions.Item label={"Attributes"}>
                            <Collapse
                                size={"small"}
                                items={[
                                    {
                                        key: "1",
                                        label: `Click to expand (${formatBytesBinary(utf8ByteLength(vulSourceItem?.attributesRaw ?? vulSourceItem?.attributes))})`,
                                        extra: renderCopyJsonButton("Attributes", vulSourceItem?.attributes, vulSourceItem?.attributesRaw),
                                        children: <div className={globalStyles["formatted-json"]} dangerouslySetInnerHTML={{__html: `${vulSourceItem?.attributes && syntaxHighlight(vulSourceItem?.attributes)}`}}/>
                                    }
                                ]}
                            />
                        </Descriptions.Item>

                        <Descriptions.Item label={"JSON data"}>
                            <Collapse
                                size={"small"}
                                onChange={onJsonDataCollapseChange}
                                items={[
                                    {
                                        key: "1",
                                        label: renderLazyJsonLabel(jsonDataLoading, jsonData, jsonDataRaw),
                                        extra: renderCopyJsonButton("JSON data", jsonData, jsonDataRaw),
                                        children: renderLazyJson(jsonDataLoading, jsonData)
                                    }
                                ]}
                            />
                        </Descriptions.Item>

                        {canManageOrgAiExtractors && (
                            <>
                                {vulSourceItem?.aiExtractionResults && vulSourceItem.aiExtractionResults.length > 0 && (
                                    <Descriptions.Item label={"AI extraction results"}>
                                        <Collapse
                                            size={"small"}
                                            items={[
                                                {
                                                    key: "1",
                                                    label: `Click to expand (${vulSourceItem.aiExtractionResults.length} results)`,
                                                    extra: (
                                                        <Tooltip title={`${globalMessagesVisible ? "Hide" : "Show"} all explanations`}>
                                                            <Button
                                                                size={"small"}
                                                                type={globalMessagesVisible ? "primary" : "default"}
                                                                icon={globalMessagesVisible ? <MessageFilled/> : <MessageOutlined/>}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setGlobalMessagesVisible(prev => !prev);
                                                                }}
                                                            />
                                                        </Tooltip>
                                                    ),
                                                    children: (
                                                        <AiExtractionResultsSection
                                                            aiExtractionResults={vulSourceItem.aiExtractionResults}
                                                            actionMode={"delete"}
                                                            onRunAgain={onSavedAiExtractionResultRunAgain}
                                                            canRunAgain={canRunAiExtractionResultAgain}
                                                            onDeleteAiExtractionResult={onDeleteAiExtractionResult}
                                                            onShowAiProviderCommunicationLog={appContext.user.isSystemAdmin() ? setAiProviderCommunicationLogRunId : undefined}
                                                            globalMessagesVisible={globalMessagesVisible}
                                                        />
                                                    )
                                                }
                                            ]}
                                        />
                                    </Descriptions.Item>
                                )}
                            </>
                        )}
                    </Descriptions>

                    <CreateTicketModal
                        visible={createTicketFormVisible}
                        onTicketCreate={() => setCreateTicketFormVisible(false)}
                        vulSourceItem={vulSourceItem}
                        onCancel={() => setCreateTicketFormVisible(false)}
                    />

                    {extractResult &&
                        <>
                            <h2>Extraction result</h2>
                            <p>
                                <Button
                                    icon={<ExperimentOutlined/>}
                                    onClick={() => {
                                        Modal.confirm({
                                            content: "Really extract and save?",
                                            okText: "Extract",
                                            cancelText: "Cancel",
                                            onOk: () => onExtractConfirm(vulSourceItem)
                                        });
                                    }}>Extract (save)</Button>
                            </p>
                            <Card size={"small"}>
                                <div className={globalStyles["formatted-json"]} dangerouslySetInnerHTML={{__html: `${extractResult && syntaxHighlight(extractResult)}`}}/>
                            </Card>
                        </>
                    }

                    {evaluateResults &&
                        <>
                            <h2>Evaluation result</h2>
                            <p>
                                <Button
                                    icon={<FunctionOutlined/>}
                                    onClick={() => {
                                        Modal.confirm({
                                            content: "Really evaluate?",
                                            okText: "Evaluate",
                                            cancelText: "Cancel",
                                            onOk: () => onEvaluateConfirm(vulSourceItem)
                                        })
                                    }}>Evaluate (save)</Button>
                            </p>
                            {evaluateResults.map(result => (
                                <Card size={"small"}>
                                    <div className={globalStyles["formatted-json"]} dangerouslySetInnerHTML={{__html: `${result && syntaxHighlight(result)}`}}/>
                                </Card>
                            ))}
                        </>
                    }

                    {aiExtractPreviewAssignments && aiExtractPreviewAssignments.length > 0 &&
                        <>
                            <h2>AI extraction results</h2>

                            <AiExtractionResultsSection
                                aiExtractionResults={aiExtractPreviewAssignments}
                                actionMode={"preview"}
                                onPreviewRunAgain={onAiExtractPreviewRunAgain}
                                onPreviewCancel={onAiExtractPreviewCancel}
                                onPreviewSave={onAiExtractPreviewSave}
                                onShowAiProviderCommunicationLog={appContext.user.isSystemAdmin() ? setAiProviderCommunicationLogRunId : undefined}
                                highlightAiExtractionResultId={highlightedPreviewId}
                                onHighlightedAiExtractionResultRendered={onHighlightedPreviewAssignmentRendered}
                            />
                        </>
                    }

                    <h2>Data and attributes changes</h2>

                    <Table className={styles['change-log-table']}
                           locale={{emptyText: "No changes available"}}
                           showSorterTooltip={false}
                           dataSource={vulSourceItem?.changeLogs}
                           size="middle"
                           pagination={false}
                           rowKey="id">

                        <Column
                            dataIndex="timestamp"
                            title={"Timestamp"}
                            width={160}
                            align={"left"}
                            render={value => formatDateTime(value)}
                        />

                        <Column
                            dataIndex={"type"}
                            title={"Type"}
                            width={100}
                            render={renderChangeLogType}
                        />

                        <Column
                            dataIndex={"data"}
                            title={"Data"}
                            render={(value, record: VulSourceItemChangeLog) => renderChangeLogData(record)}
                        />

                        <Column
                            dataIndex={"difference"}
                            title={"Difference"}
                            render={(value, record: VulSourceItemChangeLog) => renderChangeLogDifference(record)}
                        />

                        <Column
                            dataIndex={"profiles"}
                            title={"Profiles"}
                            width={170}
                            render={(value, record: VulSourceItemChangeLog) => renderItemUpdateProfileCheck(record)}
                        />

                    </Table>

                    <JsonDifferenceModal
                        open={changeLogDataModalOpen}
                        mode="value"
                        title={changeLogDataModalChangeLog
                            ? <>Change log data: {formatDateTime(changeLogDataModalChangeLog.timestamp)}</>
                            : "Change log data"}
                        valueTitle={changeLogDataModalChangeLog ? renderChangeLogType(changeLogDataModalChangeLog.type) : undefined}
                        value={changeLogDataModalChangeLog ? changeLogValue(changeLogDataModalChangeLog) : undefined}
                        onClose={() => setChangeLogDataModalOpen(false)}
                        afterOpenChange={open => {
                            if (!open) {
                                setChangeLogDataModalChangeLog(undefined);
                            }
                        }}
                    />

                    <JsonDifferenceModal
                        open={changeLogDifferenceModalOpen}
                        title={changeLogDifferenceModalChangeLog
                            ? <>Change log difference: {formatDateTime(changeLogDifferenceModalChangeLog.timestamp)}</>
                            : "Change log difference"}
                        oldTitle="Previous value"
                        newTitle="Current value"
                        oldValue={changeLogDifferenceOlderChangeLog ? changeLogValue(changeLogDifferenceOlderChangeLog) : undefined}
                        newValue={changeLogDifferenceModalChangeLog ? changeLogValue(changeLogDifferenceModalChangeLog) : undefined}
                        onClose={() => setChangeLogDifferenceModalOpen(false)}
                        afterOpenChange={open => {
                            if (!open) {
                                setChangeLogDifferenceOlderChangeLog(undefined);
                                setChangeLogDifferenceModalChangeLog(undefined);
                            }
                        }}
                    />

                    <Modal
                        open={itemUpdateProfileMatchesOpen}
                        title="Item update profile check"
                        footer={null}
                        width={900}
                        mask={{closable: false}}
                        onCancel={() => setItemUpdateProfileMatchesOpen(false)}
                    >
                        <p>
                            Evaluated at changelog timestamp: <b>{formatDateTime(itemUpdateProfileMatchesChangeLog?.timestamp)}</b>
                        </p>

                        <Table
                            dataSource={itemUpdateProfileMatches}
                            loading={itemUpdateProfileMatchesLoading}
                            rowKey="profileId"
                            pagination={false}
                            size="small"
                        >
                            <Column
                                dataIndex="profileName"
                                title="Profile"
                                render={(value, record: ItemUpdateProfileChangeLogMatch) =>
                                    <Link to={`/definitions/item-update-profiles/${record.profileId}`}>{value}</Link>
                                }
                            />
                            <Column
                                dataIndex="owner"
                                title="Owner"
                                width={160}
                                render={(value, record: ItemUpdateProfileChangeLogMatch) =>
                                    record.organizationName || (value === "SYSTEM" ? <i>System profile</i> : value)
                                }
                            />
                            <Column
                                dataIndex="matched"
                                title="Result"
                                width={110}
                                render={value => value ? <Tag color="green">matched</Tag> : <Tag>no match</Tag>}
                            />
                        </Table>
                    </Modal>

                    <h2>Related items</h2>

                    <Form layout={"inline"} className={styles['related-items-form']} onFinish={applyRelatedItemsMaxDepth} initialValues={{maxDepth: defaultRelatedItemsMaxDepth}}>
                        <Form.Item
                            name={"maxDepth"}
                            label={"Max. depth"}>
                            <InputNumber min={1} max={5}/>
                        </Form.Item>

                        <Form.Item>
                            <Button type={"primary"} htmlType={"submit"}>Apply</Button>
                        </Form.Item>
                    </Form>

                    <Table className={styles['related-items-table']}
                           showSorterTooltip={false}
                           dataSource={relatedItems}
                           locale={{emptyText: "No related items"}}
                           size="middle"
                           pagination={{
                               defaultPageSize: 25,
                               hideOnSinglePage: true,
                               showSizeChanger: false,
                           }}
                           loading={relatedItemsInProgress}
                           rowKey="id">

                        <Column
                            dataIndex="id"
                            title={"ID"}
                            width={80}
                            align={"center"}
                            render={value => <Link to={`/vul-source-items/${value}`}>
                                <span title={value}>{value.substring(0, 2) + '...' + value.substring(value.length - 2)}</span>
                            </Link>}
                        />

                        <Column
                            dataIndex={"attributes"}
                            title={"Description"}
                            render={(value, item: VulSourceItem) => <>
                                <div className={styles['description']}>
                                    <SourceTag source={item?.source}/>

                                    <SealScore item={item}/>
                                </div>

                                <VulSourceItemDescription sourceType={item.source.sourceType} attributes={value} attributeDefinitions={attributeDefinitions} hideSealScoreTag={true}/>
                            </>}
                        />

                        <Column
                            dataIndex="sourcePublished"
                            title={"Published"}
                            width={160}
                            align={"left"}
                            render={value => formatDateTime(value)}
                        />
                    </Table>

                    <AiExtractorSelectModal
                        open={aiExtractorSelectOpen}
                        aiExtractors={runnableAiExtractors}
                        initialAiExtractorId={selectedAiExtractorId}
                        showProviderCommunicationLogOption={appContext.user.isSystemAdmin()}
                        onCancel={() => setAiExtractorSelectOpen(false)}
                        onOk={(aiExtractorId: string, providerCommunicationLoggingMode: AiProviderCommunicationLoggingMode) => {
                            setSelectedAiExtractorId(aiExtractorId);
                            runAiExtractDryAndAddPreview(aiExtractorId, providerCommunicationLoggingMode);
                            setAiExtractorSelectOpen(false);
                        }}
                    />

                    <AiExtractorSelectModal
                        open={promptPreviewSelectOpen}
                        aiExtractors={aiExtractors}
                        initialAiExtractorId={selectedAiExtractorId}
                        onCancel={() => setPromptPreviewSelectOpen(false)}
                        onOk={(aiExtractorId: string) => {
                            setSelectedAiExtractorId(aiExtractorId);
                            setPromptPreviewSelectOpen(false);
                            loadPromptPreview(aiExtractorId);
                        }}
                    />

                    <PromptPreviewModal
                        open={promptPreviewOpen}
                        messages={promptPreviewMessages}
                        estimate={promptPreviewEstimate}
                        aiExtractorName={promptPreviewAiExtractorName}
                        loading={promptPreviewLoading}
                        onClose={() => setPromptPreviewOpen(false)}
                    />

                    <AiProviderCommunicationLogModal
                        open={!!aiProviderCommunicationLogRunId}
                        runId={aiProviderCommunicationLogRunId}
                        onClose={() => setAiProviderCommunicationLogRunId(undefined)}
                    />

                </Spin>
            </>
        </DocumentTitle>
    )

    function applyRelatedItemsMaxDepth(values: any) {
        setRelatedItemsMaxDepth(values.maxDepth);

        localStorage.setItem(LOCAL_STORAGE_KEY_RELATED_ITEMS_MAX_DEPTH, JSON.stringify(values.maxDepth));

        reloadRelatedItems();
    }

    function updateItem(item: VulSourceItem) {
        vulSourceItem.flags = item.flags;
        vulSourceItem.flagStats = item.flagStats;

        forceUpdate();
    }

    function onExtractConfirm(vulSourceItem: VulSourceItem) {
        vulSourceItemService.extract(vulSourceItem)
            .then((data: any) => {
                message.success("Extracting!");

                setExtractResult(data.data);

                reload();
            });
    }

    function onExtractDryConfirm(vulSourceItem: VulSourceItem) {
        vulSourceItemService.extractDry(vulSourceItem)
            .then((data: any) => {
                message.success("Extracting!");

                setExtractResult(data.data);

                reload();
            });
    }

    function onEvaluateConfirm(item: VulSourceItem) {
        vulSourceItemService.evaluate(item)
            .then((response: any) => {
                message.success("Evaluating!");

                setEvaluateResults(response.data);

                reload();
            });
    }

    function onEvaluateDryConfirm(item: VulSourceItem) {
        vulSourceItemService.evaluateDry(item)
            .then((response: any) => {
                message.success("Evaluating!");

                setEvaluateResults(response.data);

                reload();
            });
    }

    function onAiExtractPreviewSave(aiExtractionResult: AiExtractionResult) {
        if (!vulSourceItem) {
            return;
        }

        const previewResult = getPreviewAssignment(aiExtractionResult);
        if (!previewResult) {
            message.error("AI extraction preview not found.");
            return;
        }

        if (!previewResult.aiExtractorId) {
            message.error("AI extractor is missing for this preview.");
            return;
        }

        if (!previewResult.dryRunId) {
            message.error("Dry-run identifier is missing for this preview.");
            return;
        }

        Modal.confirm({
            content: "Really save this AI extraction result?",
            okText: "Save",
            cancelText: "Cancel",
            onOk: () => {
                vulSourceItemService.aiExtract(vulSourceItem, previewResult.aiExtractorId, previewResult.dryRunId)
                    .then(() => {
                        message.success("AI extraction saved.");
                        removeAiExtractPreview(previewResult.previewId);

                        reload();
                    });
            }
        });
    }

    function onAiExtractPreviewCancel(aiExtractionResult: AiExtractionResult) {
        const previewResult = getPreviewAssignment(aiExtractionResult);
        if (!previewResult) {
            return;
        }

        Modal.confirm({
            content: "Really discard this AI extraction result?",
            okText: "Discard",
            cancelText: "Cancel",
            onOk: () => {
                removeAiExtractPreview(previewResult.previewId);
            }
        });
    }

    function onAiExtractPreviewRunAgain(aiExtractionResult: AiExtractionResult) {
        const previewResult = getPreviewAssignment(aiExtractionResult);
        if (!previewResult) {
            message.error("AI extraction preview not found.");
            return;
        }

        if (!previewResult.aiExtractorId) {
            message.error("AI extractor is missing for this preview.");
            return;
        }

        const aiExtractorId = previewResult.aiExtractorId;

        confirmAiExtractRunAgain(aiExtractorId);
    }

    function onSavedAiExtractionResultRunAgain(aiExtractionResult: AiExtractionResult) {
        const aiExtractorId = aiExtractionResult.aiExtractorId;

        if (!aiExtractorId) {
            return;
        }

        confirmAiExtractRunAgain(aiExtractorId);
    }

    function confirmAiExtractRunAgain(aiExtractorId: string) {
        Modal.confirm({
            content: "Really run AI extraction again?",
            okText: "Run again",
            cancelText: "Cancel",
            onOk: () => {
                runAiExtractDryAndAddPreview(aiExtractorId);
            }
        });
    }

    function canRunAiExtractionResultAgain(aiExtractionResult: AiExtractionResult): boolean {
        const aiExtractorId = normalizeNonEmptyString(aiExtractionResult.aiExtractorId);

        return aiExtractorId !== undefined && runnableAiExtractors.some(value => value.id === aiExtractorId);
    }

    function runAiExtractDryAndAddPreview(aiExtractorId: string, providerCommunicationLoggingMode: AiProviderCommunicationLoggingMode = "DEFAULT") {
        if (!vulSourceItem) {
            return;
        }

        vulSourceItemService.aiExtractDry(vulSourceItem, aiExtractorId, providerCommunicationLoggingMode)
            .then((data: AiExtractDryRunResponse) => {
                message.success("AI extraction (dry-run) finished.");

                const previewResult = buildDryRunPreviewAssignment(aiExtractorId, data);

                setAiExtractPreviewAssignments(previous => {
                    const values = previous ?? [];
                    return [previewResult, ...values.filter(value => value.previewId !== previewResult.previewId)];
                });

                startPreviewHighlight(previewResult.previewId);
            })
            .catch((error: any) => {
                if (error?.response?.status === 502) {
                    const data = error?.response?.data;
                    const detail = data?.detail;
                    const errorMessage = `AI extraction failed: ${detail}`;

                    if (data?.aiProviderCommunicationRunId) {
                        setAiProviderCommunicationLogRunId(data.aiProviderCommunicationRunId);
                    }

                    message.error(errorMessage, 5);
                }
            });
    }

    function buildDryRunPreviewAssignment(aiExtractorId: string, data: AiExtractDryRunResponse): DryRunPreviewAssignment {
        const aiExtractor = aiExtractors.find(value => value.id === aiExtractorId);
        const dryRunId = normalizeNonEmptyString(data?.dryRunId);
        const previewId = dryRunId ?? createPreviewId();

        return {
            id: previewId,
            previewId,
            dryRunId,
            aiExtractorId,
            aiExtractorName: data?.aiExtractorConfig?.name ?? aiExtractor?.name ?? "AI extractor",
            extractedAt: new Date(),
            aiAttributes: data?.aiAttributes,
            tokenUsage: data?.tokenUsage,
            executionTimeMs: data?.executionTimeMs,
            reasoningSummary: data?.reasoningSummary,
            toolTelemetry: data?.toolTelemetry,
            aiExtractorConfig: data?.aiExtractorConfig,
            priceBreakdown: data?.priceBreakdown,
            aiProviderCommunicationRunId: data?.aiProviderCommunicationRunId
        };
    }

    function getPreviewAssignment(aiExtractionResult: AiExtractionResult): DryRunPreviewAssignment | undefined {
        return aiExtractPreviewAssignments?.find(value => value.id === aiExtractionResult.id);
    }

    function removeAiExtractPreview(previewId: string) {
        setAiExtractPreviewAssignments(previous => {
            if (!previous) {
                return previous;
            }

            const values = previous.filter(value => value.previewId !== previewId);
            return values.length > 0 ? values : undefined;
        });

        if (lastScrolledPreviewIdRef.current === previewId) {
            lastScrolledPreviewIdRef.current = undefined;
        }

        setHighlightedPreviewId(previous => previous === previewId ? undefined : previous);
    }

    function onHighlightedPreviewAssignmentRendered(element: HTMLDivElement | null) {
        if (!element || !highlightedPreviewId) {
            return;
        }

        if (lastScrolledPreviewIdRef.current === highlightedPreviewId) {
            return;
        }

        lastScrolledPreviewIdRef.current = highlightedPreviewId;

        element.scrollIntoView({
            behavior: "smooth",
            block: "center"
        });
    }

    function startPreviewHighlight(previewId: string) {
        clearPreviewHighlightTimeout();

        lastScrolledPreviewIdRef.current = undefined;
        setHighlightedPreviewId(previewId);

        previewHighlightTimeoutRef.current = window.setTimeout(() => {
            setHighlightedPreviewId(previous => previous === previewId ? undefined : previous);

            if (lastScrolledPreviewIdRef.current === previewId) {
                lastScrolledPreviewIdRef.current = undefined;
            }

            previewHighlightTimeoutRef.current = undefined;
        }, PREVIEW_HIGHLIGHT_DURATION_MS);
    }

    function clearPreviewHighlightTimeout() {
        if (previewHighlightTimeoutRef.current !== undefined) {
            window.clearTimeout(previewHighlightTimeoutRef.current);
            previewHighlightTimeoutRef.current = undefined;
        }
    }

    function createPreviewId(): string {
        return `preview-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    }

    function onAiExtractDryConfirm(item: VulSourceItem) {
        if (!item) {
            return;
        }

        if (!runnableAiExtractors.length) {
            message.error("No AI extractor is available for AI extraction.");
            return;
        }

        setAiExtractorSelectOpen(true);
    }

    function onPromptPreviewConfirm(item: VulSourceItem) {
        if (!item) {
            return;
        }

        if (!aiExtractors.length) {
            message.error("No AI extractor is defined.");
            return;
        }

        setPromptPreviewSelectOpen(true);
    }

    function onJsonDataCollapseChange(activeKeys: string | string[]) {
        if (isCollapsePanelActive(activeKeys, "1")) {
            loadJsonData();
        }
    }

    function isCollapsePanelActive(activeKeys: string | string[], key: string) {
        return Array.isArray(activeKeys) ? activeKeys.includes(key) : activeKeys === key;
    }

    function loadJsonData() {
        if (!vulSourceItem?.id || jsonData !== undefined || jsonDataLoading) {
            return;
        }

        const itemId = vulSourceItem.id;

        setJsonDataLoading(true);

        vulSourceItemService.getJsonData(itemId)
            .then(data => {
                if (!isCurrentVulSourceItem(itemId)) {
                    return;
                }

                setJsonDataRaw(data.raw);
                setJsonData(data.parsed);
            })
            .catch(() => {
                if (isCurrentVulSourceItem(itemId)) {
                    message.error("Failed to load JSON data.");
                }
            })
            .finally(() => {
                if (isCurrentVulSourceItem(itemId)) {
                    setJsonDataLoading(false);
                }
            });
    }

    function isCurrentVulSourceItem(itemId: string) {
        return currentVulSourceItemIdRef.current === itemId;
    }

    function renderLazyJsonLabel(loading: boolean, value?: any, rawValue?: string) {
        if (loading) {
            return "Loading...";
        }

        const text = formatJsonForClipboard(value, rawValue);

        if (!text) {
            return "Click to load";
        }

        return `Click to expand (${formatBytesBinary(utf8ByteLength(text))})`;
    }

    function renderLazyJson(loading: boolean, value?: any) {
        if (loading) {
            return <Spin indicator={<LoadingOutlined style={{fontSize: 24}} spin={true}/>} />;
        }

        if (value === undefined || value === null) {
            return <i>Data not loaded.</i>;
        }

        return <div className={globalStyles["formatted-json"]} dangerouslySetInnerHTML={{__html: syntaxHighlight(value)}}/>;
    }

    function renderCopyJsonButton(label: string, value?: any, rawValue?: string) {
        const text = formatJsonForClipboard(value, rawValue);

        if (!text) {
            return null;
        }

        return (
            <Tooltip title={`Copy ${label}`}>
                <Button
                    icon={<CopyOutlined/>}
                    size={"small"}
                    onClick={(e) => {
                        e.stopPropagation();

                        navigator.clipboard.writeText(text).then(() => {
                            message.info(`${label} copied to clipboard.`);
                        });
                    }}
                />
            </Tooltip>
        );
    }

    function formatJsonForClipboard(value?: any, rawValue?: string) {
        if (rawValue) {
            return rawValue;
        }

        if (value === undefined || value === null) {
            return "";
        }

        if (typeof value === "string") {
            return value;
        }

        return JSON.stringify(value, null, 2);
    }

    function loadPromptPreview(aiExtractorId: string) {
        if (!vulSourceItem) {
            return;
        }

        const aiExtractor = aiExtractors.find(value => value.id === aiExtractorId);

        setPromptPreviewAiExtractorName(aiExtractor?.name);
        setPromptPreviewMessages(undefined);
        setPromptPreviewEstimate(undefined);
        setPromptPreviewLoading(true);
        setPromptPreviewOpen(true);

        vulSourceItemService.getPromptPreview(vulSourceItem, aiExtractorId)
            .then(data => {
                setPromptPreviewMessages(data.messages);
                setPromptPreviewEstimate(data.estimate);
            })
            .catch(() => {
                message.error("Failed to load prompt preview.");
                setPromptPreviewOpen(false);
            })
            .finally(() => {
                setPromptPreviewLoading(false);
            });
    }

    function renderChangeLogType(type: VulSourceItemChangeType) {
        switch (type) {
            case VulSourceItemChangeType.JSON_DATA:
                return "JSON data";
            case VulSourceItemChangeType.ATTRIBUTES:
                return "attributes";
        }
    }

    function renderItemUpdateProfileCheck(changeLog: VulSourceItemChangeLog) {
        if (changeLog.type !== VulSourceItemChangeType.ATTRIBUTES || changeLog.changeKind !== VulSourceItemChangeKind.UPDATED) {
            return null;
        }

        return (
            <Button
                size="small"
                onClick={() => onItemUpdateProfileCheck(changeLog)}
            >
                Evaluate profiles
            </Button>
        );
    }

    function renderChangeLogData(changeLog: VulSourceItemChangeLog) {
        return (
            <Button size="small" onClick={() => {
                openChangeLogData(changeLog);
            }}>
                View data ({changeLogValueSize(changeLog)})
            </Button>
        );
    }

    function renderChangeLogDifference(changeLog: VulSourceItemChangeLog) {
        const olderChangeLog = vulSourceItem?.findOlderChangeLog(changeLog);

        if (olderChangeLog === undefined) {
            return <i>no difference available</i>;
        }

        return (
            <Button size="small" onClick={() => {
                openChangeLogDifference(changeLog);
            }}>
                View difference
            </Button>
        );
    }

    function openChangeLogData(changeLog: VulSourceItemChangeLog) {
        loadChangeLogData(changeLog).then(loadedChangeLog => {
            if (!loadedChangeLog) {
                return;
            }

            setChangeLogDataModalChangeLog(loadedChangeLog);
            setChangeLogDataModalOpen(true);
        });
    }

    function openChangeLogDifference(changeLog: VulSourceItemChangeLog) {
        const olderChangeLog = vulSourceItem?.findOlderChangeLog(changeLog);

        if (olderChangeLog === undefined) {
            return;
        }

        Promise.all([
            loadChangeLogData(olderChangeLog),
            loadChangeLogData(changeLog)
        ]).then(([loadedOlderChangeLog, loadedChangeLog]) => {
            if (!loadedOlderChangeLog || !loadedChangeLog) {
                return;
            }

            setChangeLogDifferenceOlderChangeLog(loadedOlderChangeLog);
            setChangeLogDifferenceModalChangeLog(loadedChangeLog);
            setChangeLogDifferenceModalOpen(true);
        });
    }

    function loadChangeLogData(changeLog: VulSourceItemChangeLog): Promise<VulSourceItemChangeLog | undefined> {
        if (!vulSourceItem?.id || !changeLog.id) {
            return Promise.resolve(undefined);
        }

        if (isChangeLogDataLoaded(changeLog)) {
            return Promise.resolve(changeLog);
        }

        return vulSourceItemService.getChangeLogData(vulSourceItem.id, changeLog.id)
            .then(data => {
                return Object.assign(new VulSourceItemChangeLog(), changeLog, {
                    jsonDataRaw: data.jsonDataRaw,
                    jsonData: data.jsonData,
                    attributesRaw: data.attributesRaw,
                    attributes: data.attributes
                });
            })
            .catch(() => {
                message.error("Failed to load change log data.");

                return undefined;
            });
    }

    function isChangeLogDataLoaded(changeLog: VulSourceItemChangeLog) {
        switch (changeLog.type) {
            case VulSourceItemChangeType.JSON_DATA:
                return changeLog.jsonDataRaw !== undefined;
            case VulSourceItemChangeType.ATTRIBUTES:
                return changeLog.attributesRaw !== undefined;
        }
    }

    function changeLogValue(changeLog: VulSourceItemChangeLog) {
        switch (changeLog.type) {
            case VulSourceItemChangeType.JSON_DATA:
                return changeLog.jsonData;
            case VulSourceItemChangeType.ATTRIBUTES:
                return changeLog.attributes;
        }
    }

    function changeLogValueSize(changeLog: VulSourceItemChangeLog) {
        switch (changeLog.type) {
            case VulSourceItemChangeType.JSON_DATA:
                if (changeLog.jsonDataSize !== undefined) {
                    return formatBytesBinary(changeLog.jsonDataSize);
                }

                return formatBytesBinary(utf8ByteLength(changeLog.jsonDataRaw ?? changeLog.jsonData));
            case VulSourceItemChangeType.ATTRIBUTES:
                if (changeLog.attributesSize !== undefined) {
                    return formatBytesBinary(changeLog.attributesSize);
                }

                return formatBytesBinary(utf8ByteLength(changeLog.attributesRaw ?? changeLog.attributes));
        }
    }

    function onItemUpdateProfileCheck(changeLog: VulSourceItemChangeLog) {
        if (!vulSourceItem) {
            return;
        }

        setItemUpdateProfileMatchesOpen(true);
        setItemUpdateProfileMatchesLoading(true);
        setItemUpdateProfileMatches([]);
        setItemUpdateProfileMatchesChangeLog(changeLog);

        vulSourceItemService.getItemUpdateProfileChangeLogMatches(vulSourceItem, changeLog)
            .then(matches => setItemUpdateProfileMatches(sortProfileMatches(matches)))
            .catch(() => message.error("Item update profile check failed."))
            .finally(() => setItemUpdateProfileMatchesLoading(false));
    }

    function sortProfileMatches(matches: ItemUpdateProfileChangeLogMatch[]) {
        return [...matches].sort((a, b) => {
            if (a.matched !== b.matched) {
                return a.matched ? -1 : 1;
            }

            return a.profileName.localeCompare(b.profileName);
        });
    }

    function reload() {
        setJsonDataRaw(undefined);
        setJsonData(undefined);
        setJsonDataLoading(false);

        vulSourceItemService.get(vulSourceItemId).then(item => {
            setVulSourceItem(item);

            editForm.setFieldsValue(item);

            if (!canManageOrgAiExtractors) {
                setAiExtractors([]);
                return;
            }

            aiExtractorService.getList(QueryOptions.newUnlimitedOrderedInstance("name"), item.source?.id)
                .then(result => {
                    setAiExtractors(result.data);
                });
        });
    }

    function reloadRelatedItems() {
        setRelatedItemsInProgress(true);

        Promise
            .all([
                vulSourceItemService.getRelatedItems(vulSourceItemId, relatedItemsMaxDepth),
                vulSourceItemService.getRelatedTickets(vulSourceItemId, relatedItemsMaxDepth)
            ])
            .then(values => {
                const [items, tickets] = values;

                setRelatedItems(items);
                setRelatedTickets(tickets);

                setMaxRelatedSealScore(items.map(item => item.attributes.sealScore).filter(item => item !== undefined).sort().pop());
            })
            .finally(() => setRelatedItemsInProgress(false));
    }

    function onDeleteAiExtractionResult(aiExtractionResultId: string) {
        if (!vulSourceItem) {
            return;
        }

        Modal.confirm({
            content: "Do you really want to delete this AI extraction result?",
            okText: "Delete",
            cancelText: "Cancel",
            okButtonProps: {danger: true},
            onOk: () => {
                vulSourceItemService.deleteAiExtractionResult(vulSourceItem, aiExtractionResultId)
                    .then(() => {
                        message.success("AI extraction result deleted.");

                        reload();
                    });
            }
        });
    }

    function renderDescriptionSection() {
        return <>
            <div className={styles['description']}>
                <SourceTag source={vulSourceItem?.source}/>

                <SealScore item={vulSourceItem}/>

                <SealScore label="Max. related score" score={maxRelatedSealScore}/>
            </div>

            <AiAttributeEntriesBlock
                item={vulSourceItem}
                aiAttributeTagClassName={styles["class-tag"]}
                aiAttributeTagInteractiveClassName={styles["class-tag-interactive"]}
            />

            <VulSourceItemDescription
                sourceType={vulSourceItem?.source?.sourceType}
                attributes={vulSourceItem?.attributes}
                affected={vulSourceItem?.affected}
                attributeDefinitions={attributeDefinitions}
                hideSealScoreTag={true}
                framed={true}
            />
        </>;
    }

}

export default VulSourceItemDetail;
